import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ShieldAlert, Inbox, Trash2, ExternalLink } from "lucide-react";

const FILTERS = [
  { key: "approved", label: "Live" }, { key: "rejected", label: "Taken down" }, { key: "", label: "All" },
];
const BADGE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-green-50 text-green-800 border-green-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};

export default function AdminListings() {
  const [filter, setFilter] = useState("approved");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    let stale = false;
    setLoading(true);
    api.get("/admin/listings", { params: filter ? { status: filter } : {} })
      .then((r) => { if (!stale) setRows(r.data); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-listings-page">
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><ShieldAlert className="w-3 h-3" /> Admin</p>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Listings.</h1>
      <div className="flex gap-2 flex-wrap my-6">
        {FILTERS.map((f) => (
          <button key={f.key || "all"} onClick={() => setFilter(f.key)} data-testid={`admin-filter-${f.key || "all"}`}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border ${filter === f.key ? "bg-black text-white border-black" : "bg-white border-zinc-200 hover:border-zinc-400"}`}>
            {f.label}
          </button>
        ))}
      </div>
      {loading ? <p className="text-zinc-500">Loading...</p> : rows.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-500" data-testid="admin-empty">
          <Inbox className="w-10 h-10 mx-auto text-zinc-300 mb-2" /> Nothing here.
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-listings-list">
          {rows.map((l) => <ReviewCard key={l.id} l={l} onDone={load} />)}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ l, onDone }) {
  const [note, setNote] = useState(l.admin_note || "");
  const act = async (status) => {
    try {
      await api.put(`/admin/listings/${l.id}`, { status, admin_note: status === "approved" ? null : note || null });
      toast.success(status === "approved" ? "Listing restored" : "Listing taken down");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${l.business_name}? This cannot be undone.`)) return;
    await api.delete(`/admin/listings/${l.id}`);
    toast.success("Listing deleted");
    onDone();
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={`admin-listing-${l.id}`}>
      <div className="flex justify-between gap-4 flex-wrap">
        <div>
          <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${BADGE[l.status]}`}>{l.status === "rejected" ? "Taken down" : "Live"}</span>
          <p className="font-display text-xl font-bold mt-2">{l.business_name}</p>
          <p className="text-sm text-zinc-500">{l.category} · {l.town} {l.postcode} · {l.email}{l.phone && ` · ${l.phone}`}</p>
        </div>
        <Link to={`/b/${l.id}`} target="_blank" className="text-sm underline inline-flex items-center gap-1 self-start" data-testid={`admin-preview-${l.id}`}>
          Preview <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>
      <p className="text-sm text-zinc-700 mt-3 line-clamp-3 whitespace-pre-wrap">{l.description}</p>
      {l.photos?.length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {l.photos.map((p) => <img key={p} src={mediaUrl(p)} alt="" className="w-16 h-16 object-cover rounded border border-zinc-200" />)}
        </div>
      )}
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-4"
        placeholder="Note to the business (optional, included in their email)" data-testid={`admin-note-${l.id}`} />
      <div className="flex gap-2 mt-3 flex-wrap">
        {l.status !== "approved" && <Button onClick={() => act("approved")} className="bg-black text-white hover:bg-zinc-800" data-testid={`admin-approve-${l.id}`}>Restore</Button>}
        {l.status !== "rejected" && <Button variant="outline" onClick={() => act("rejected")} data-testid={`admin-reject-${l.id}`}>Take down</Button>}
        <Button variant="ghost" onClick={remove} className="text-red-600 ml-auto" data-testid={`admin-delete-${l.id}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
      </div>
    </div>
  );
}
